import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import api from "@/utils/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Save } from "lucide-react";

const inp = "h-8 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#159DFF] w-full";
const EMPTY = { code: "", name: "", category: "ADDON", default_description: "", uom: "lumpsum", sac_code: "", default_gst_rate: 18, is_active: true };

const TERM_KEYS = ["COMMON", "COURIER", "CARGO", "PTL", "FTL", "RELOCATION"];
const TERM_HELP = {
  COMMON: "Sendtoo shipping terms. Prints on every quotation, first.",
  COURIER: "Prints when a Courier service is on the quote.",
  CARGO: "Prints when an Air Cargo service is on the quote.",
  PTL: "Prints when a PTL service is on the quote.",
  FTL: "Prints when an FTL service is on the quote.",
  RELOCATION: "Prints when a Relocation service is on the quote.",
};

function TermsTemplates() {
  const [rows, setRows] = useState({});
  const [text, setText] = useState({});
  const [titles, setTitles] = useState({});
  const [busy, setBusy] = useState("");
  const load = async () => {
    try {
      const r = await api.get("/v2/quotations/terms");
      const m = {}, t = {}, ti = {};
      (r.data.items || []).forEach((x) => { m[x.key] = x; t[x.key] = (x.items || []).join("\n"); ti[x.key] = x.title; });
      setRows(m); setText(t); setTitles(ti);
    } catch { toast.error("Could not load terms templates"); }
  };
  useEffect(() => { load(); }, []);
  const restore = async (k) => {
    if (!window.confirm(`Replace the ${k} terms with the standard Sendtoo wording? Your edits to this section will be lost (saved quotes are not affected).`)) return;
    setBusy(k);
    try { await api.post(`/v2/quotations/terms/${k}/reset`); toast.success(`${k} terms restored`); load(); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Restore failed (admin only)"); }
    finally { setBusy(""); }
  };
  const save = async (k) => {
    setBusy(k);
    try {
      await api.put(`/v2/quotations/terms/${k}`, { title: titles[k] || k, items: (text[k] || "").split("\n").map((x) => x.trim()).filter(Boolean) });
      toast.success(`Saved ${k} terms`); load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed (admin only)"); }
    finally { setBusy(""); }
  };
  return (
    <div className="mt-8" data-testid="terms-templates">
      <h2 className="text-lg font-bold text-slate-900">Terms &amp; conditions templates</h2>
      <p className="text-sm text-slate-500 mb-4">Linked to the service category above. When a quote has a PTL, FTL or Relocation service, its terms are added automatically after the general terms. One point per line; write <b>Heading :: text</b> to print the heading in bold. Changes apply to <b>new</b> quotes; saved quotes keep their own copy. These are draft wording — have them reviewed before relying on them.</p>
      <div className="grid lg:grid-cols-2 gap-4">
        {TERM_KEYS.map((k) => (
          <div key={k} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold bg-slate-100 text-slate-700 rounded px-2 py-0.5">{k}</span>
              <input className={inp} value={titles[k] ?? ""} placeholder="Section heading" onChange={(e) => setTitles({ ...titles, [k]: e.target.value })} />
            </div>
            <p className="text-xs text-slate-500 mb-2">{TERM_HELP[k]}{!rows[k] && " No terms yet — only the general terms print for this category."}</p>
            <textarea rows={k === "COMMON" ? 22 : 9} className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#159DFF]"
              value={text[k] ?? ""} onChange={(e) => setText({ ...text, [k]: e.target.value })} />
            <div className="mt-2 flex items-center justify-end gap-3">
              <button type="button" className="text-xs text-slate-500 hover:text-[#159DFF] hover:underline" disabled={busy === k} onClick={() => restore(k)}>Restore standard wording</button>
              <Button size="sm" disabled={busy === k} onClick={() => save(k)}><Save className="w-4 h-4 mr-1" />Save</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function QuotationCatalog() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], categories: [], uoms: [] });
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try { setData((await api.get("/v2/quotations/catalog", { params: { include_inactive: true } })).data); }
    catch { toast.error("Could not load catalog"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const val = (it, k) => (edits[it.id] && k in edits[it.id] ? edits[it.id][k] : it[k]);
  const set = (it, k, v) => setEdits((e) => ({ ...e, [it.id]: { ...(e[it.id] || {}), [k]: v } }));
  const saveRow = async (it) => {
    try { await api.put(`/v2/quotations/catalog/${it.id}`, edits[it.id]); toast.success(`Saved ${it.name}`); setEdits(({ [it.id]: _x, ...rest }) => rest); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed (admin only)"); }
  };
  const create = async () => {
    try { await api.post("/v2/quotations/catalog", { ...draft, default_gst_rate: Number(draft.default_gst_rate) }); toast.success("Service added"); setAdding(false); setDraft(EMPTY); load(); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not add service"); }
  };

  return (
    <DashboardLayout title="Service catalog">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="quotation-catalog">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate("/admin/quotations")} className="text-slate-500 hover:text-slate-800"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Service catalog</h1>
          <Button size="sm" className="ml-auto bg-[#159DFF] hover:bg-[#0F84D9] text-white" onClick={() => setAdding((a) => !a)}><Plus className="w-4 h-4 mr-1.5" />Add service</Button>
        </div>
        <p className="text-sm text-slate-500 mb-4">One list for every quotation. Changes apply to <b>new</b> quotes only. SAC codes and default GST are starting points — confirm them with your CA.</p>

        {adding && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid sm:grid-cols-6 gap-2">
            <input className={inp} placeholder="Code (e.g. FTL-32FT)" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
            <input className={inp + " sm:col-span-2"} placeholder="Service name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <select className={inp} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>{data.categories.map((c) => <option key={c}>{c}</option>)}</select>
            <select className={inp} value={draft.uom} onChange={(e) => setDraft({ ...draft, uom: e.target.value })}>{data.uoms.map((c) => <option key={c}>{c}</option>)}</select>
            <input className={inp} placeholder="SAC" value={draft.sac_code} onChange={(e) => setDraft({ ...draft, sac_code: e.target.value })} />
            <input className={inp + " sm:col-span-4"} placeholder="Default description" value={draft.default_description} onChange={(e) => setDraft({ ...draft, default_description: e.target.value })} />
            <select className={inp} value={draft.default_gst_rate} onChange={(e) => setDraft({ ...draft, default_gst_rate: e.target.value })}><option value={5}>5%</option><option value={18}>18%</option></select>
            <Button size="sm" onClick={create}>Create</Button>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Code", "Service", "Category", "Unit", "SAC", "GST", "Default description", "Active", ""].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>}
              {!loading && data.items.map((it) => (
                <tr key={it.id} className={`border-t border-slate-100 ${val(it, "is_active") ? "" : "opacity-50"}`}>
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{it.code}</td>
                  <td className="px-3 py-2 min-w-[160px]"><input className={inp} value={val(it, "name")} onChange={(e) => set(it, "name", e.target.value)} /></td>
                  <td className="px-3 py-2"><select className={inp} value={val(it, "category")} onChange={(e) => set(it, "category", e.target.value)}>{data.categories.map((c) => <option key={c}>{c}</option>)}</select></td>
                  <td className="px-3 py-2"><select className={inp} value={val(it, "uom")} onChange={(e) => set(it, "uom", e.target.value)}>{data.uoms.map((c) => <option key={c}>{c}</option>)}</select></td>
                  <td className="px-3 py-2 w-24"><input className={inp} value={val(it, "sac_code")} onChange={(e) => set(it, "sac_code", e.target.value)} /></td>
                  <td className="px-3 py-2 w-20"><select className={inp} value={val(it, "default_gst_rate")} onChange={(e) => set(it, "default_gst_rate", Number(e.target.value))}><option value={5}>5%</option><option value={18}>18%</option></select></td>
                  <td className="px-3 py-2 min-w-[220px]"><input className={inp} value={val(it, "default_description")} onChange={(e) => set(it, "default_description", e.target.value)} /></td>
                  <td className="px-3 py-2"><input type="checkbox" checked={!!val(it, "is_active")} onChange={(e) => set(it, "is_active", e.target.checked)} /></td>
                  <td className="px-3 py-2">{edits[it.id] && <Button size="sm" onClick={() => saveRow(it)}><Save className="w-4 h-4 mr-1" />Save</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <TermsTemplates />
      </div>
    </DashboardLayout>
  );
}
